import React, {FC, useEffect, useState} from "react"

import {Body, H1, H4, H6, Label} from "@irati/ui/src/Typography"
import {MediaRental, WellnessMedia, WellnessMediaType} from "@irati/types"
import tracking from "@irati-tracking/wellness"
import mainTheme from "@irati/ui/src/global/theme"
import {FormattedMessage} from "@irati/utils/lang"
import {getMediaTypeCopy, upcomingEventDateAndTime, liveVideoDayText, startedStreaming} from "@irati/wellness/utils"
import Markdown from "@irati/ui/src/Markdown"
import Badge from "@irati/ui/src/Badge"
import Spacing from "@irati/ui/src/Spacing"
import moment from "moment-timezone"
import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"
import wellnessService from "@irati/wellness/services/Wellness"
import {IconTypes} from "@irati/ui/src/Icon"
import {IconButton} from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"

import MediaImportantTags from "../MediaImportantTags"
import {Card} from "./styled"

interface Props {
  media: WellnessMedia
  liveVideoInProgress: boolean
  isPremiumContent: boolean
  onShowAlert: () => void
}

const Index: FC<Props> = ({media, liveVideoInProgress, isPremiumContent, onShowAlert}) => {
  const {author, title, description, liveVideoStartTime, liveVideoEndTime, uuid} = media

  const [timestampString, setTimestampString] = useState<string>("")
  const [expiresAt, setExpiresAt] = useState<string>("")
  const [load, setLoad] = useState<Promise<any> | null>(hangPromise())
  const [shareAnimated, setShareAnimated] = useState(true)

  const fetchPremium = async () => {
    const loadData = await wellnessService.wellnessMediaRentals()
    const expiryDate = loadData?.results?.find((m: MediaRental) => m.mediaId === uuid)?.expiresAt

    setExpiresAt(expiryDate)
  }

  const handleOnIconClick = () => {
    setShareAnimated(false)
    onShowAlert()
  }

  useEffect(() => {
    setLoad(fetchPremium())
    const timer = setInterval(() => setTimestampString(startedStreaming(liveVideoStartTime)), 60000)
    setTimestampString(startedStreaming(liveVideoStartTime))
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    tracking.viewMediaContent(media)
  }, [uuid])
  return (
    <Card marginTop={4}>
      <Flex width="100%" justifyContent="flex-end" marginBottom>
        <IconButton
          icon={shareAnimated ? IconTypes.shareAnimated : IconTypes.share}
          onClick={handleOnIconClick}
          margin={[-1, -1, 0, 0]}
          size={24}
        />
      </Flex>
      {media.type !== WellnessMediaType.LiveVideo && isPremiumContent && (
        <Spacing marginBottom={2}>
          <Badge color={mainTheme.colors.primary1} uppercase weight="semibold">
            <FormattedMessage id="wellness.rentalMedia.premium" />
          </Badge>
        </Spacing>
      )}
      {media.type === WellnessMediaType.LiveVideo && !liveVideoInProgress && (
        <Body color={mainTheme.colors.utility.contrast} marginBottom={2}>
          <FormattedMessage id="platform.liveVideo.liveOn" />
          {liveVideoDayText(liveVideoStartTime)}, {upcomingEventDateAndTime(liveVideoStartTime, liveVideoEndTime)}
        </Body>
      )}
      {media.type === WellnessMediaType.LiveVideo && liveVideoInProgress && (
        <Body color={mainTheme.colors.utility.contrast} marginBottom={2}>
          <FormattedMessage id="platform.liveVideo.startedStreaming" /> {timestampString}
        </Body>
      )}
      {author && <Label large>{author.name}</Label>}
      <H1>{title}</H1>
      <MediaImportantTags media={media} color={mainTheme.colors.utility.contrast} />

      {media.type !== WellnessMediaType.LiveVideo && isPremiumContent && !!media?.src?.length && (
        <Loading load={load}>
          <H6 marginTop={2}>
            <FormattedMessage id="wellness.rentalMedia.countdown" values={{remainingTime: moment(expiresAt).toNow(true)}} />
          </H6>
        </Loading>
      )}

      <H4 marginTop={5} marginBottom>
        <FormattedMessage id="wellness.media.description" values={{mediaType: getMediaTypeCopy(media.type)}} />
      </H4>

      <Markdown source={description} />
      {author && (
        <>
          <H4 marginBottom marginTop={3}>
            <FormattedMessage id="wellness.media.author.description" />
          </H4>
          <Markdown source={author.description} />
        </>
      )}
    </Card>
  )
}

export default Index
